import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../config/api';

const baseUrl = API_BASE_URL;

interface UploadResult {
  ok: boolean;
  job_id: number;
  source: string;
}

async function uploadFile(file: File, targetFilePath?: string): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  if (targetFilePath) form.append('target_file_path', targetFilePath);
  const res = await authFetch(`${baseUrl}/api/knowledge/ingest`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function submitUrl(url: string, targetFilePath?: string): Promise<UploadResult> {
  const res = await authFetch(`${baseUrl}/api/knowledge/ingest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, ...(targetFilePath ? { target_file_path: targetFilePath } : {}) }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

interface Props {
  onJobCreated: (jobId: number) => void;
}

export default function DocumentUpload({ onJobCreated }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [mode, setMode] = useState<'file' | 'url'>('file');
  const fileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Target picker state
  const [showTarget, setShowTarget] = useState(false);
  const [targetFile, setTargetFile] = useState('');
  const [targetTitle, setTargetTitle] = useState('');
  const [vaultFiles, setVaultFiles] = useState<{ file_path: string; title: string }[] | null>(null);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [fileFilter, setFileFilter] = useState('');

  // Fetch vault files when section is expanded
  useEffect(() => {
    if (!showTarget || vaultFiles !== null) return;
    setVaultLoading(true);
    authFetch(`${baseUrl}/api/knowledge/vault/files`)
      .then((r) => r.json())
      .then((data) => {
        const files: { file_path: string; title: string }[] = data.files ?? [];
        setVaultFiles(files);
      })
      .catch(() => setVaultFiles([]))
      .finally(() => setVaultLoading(false));
  }, [showTarget]);

  const filteredFiles = (vaultFiles ?? []).filter((f) =>
    f.title.toLowerCase().includes(fileFilter.toLowerCase()) ||
    f.file_path.toLowerCase().includes(fileFilter.toLowerCase())
  );

  const fileMut = useMutation(
    (file: File) => uploadFile(file, targetFile || undefined),
    {
      onSuccess: (data) => {
        onJobCreated(data.job_id);
        queryClient.invalidateQueries(['ingestionJobs']);
      },
    }
  );

  const urlMut = useMutation(
    (url: string) => submitUrl(url, targetFile || undefined),
    {
      onSuccess: (data) => {
        onJobCreated(data.job_id);
        setUrlInput('');
        queryClient.invalidateQueries(['ingestionJobs']);
      },
    }
  );

  const isLoading = fileMut.isLoading || urlMut.isLoading;
  const error = fileMut.error || urlMut.error;

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) fileMut.mutate(file);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) fileMut.mutate(file);
  }

  return (
    <div className="doc-upload">
      <div className="doc-upload__tabs">
        <button
          className={`doc-upload__tab ${mode === 'file' ? 'doc-upload__tab--active' : ''}`}
          onClick={() => setMode('file')}
        >
          Upload File
        </button>
        <button
          className={`doc-upload__tab ${mode === 'url' ? 'doc-upload__tab--active' : ''}`}
          onClick={() => setMode('url')}
        >
          From URL
        </button>
      </div>

      {mode === 'file' ? (
        <div
          className={`doc-upload__drop ${dragOver ? 'doc-upload__drop--active' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.doc,.txt"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {isLoading ? (
            <span>Uploading...</span>
          ) : (
            <>
              <span className="doc-upload__icon">+</span>
              <span>Drop a file here or click to browse</span>
              <span className="doc-upload__hint">PDF, DOCX, TXT (max 50MB)</span>
            </>
          )}
        </div>
      ) : (
        <div className="doc-upload__url-form">
          <input
            type="url"
            className="doc-upload__url-input"
            placeholder="https://www.efsa.europa.eu/..."
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            disabled={isLoading}
          />
          <button
            className="btn btn--approve"
            onClick={() => urlInput && urlMut.mutate(urlInput)}
            disabled={isLoading || !urlInput}
          >
            {isLoading ? 'Submitting...' : 'Ingest'}
          </button>
        </div>
      )}

      {/* Target location picker */}
      <div className="doc-upload__target">
        <button
          className="doc-upload__target-toggle"
          onClick={() => setShowTarget((v) => !v)}
          type="button"
        >
          <span className={`doc-upload__target-arrow ${showTarget ? 'doc-upload__target-arrow--open' : ''}`}>▶</span>
          Target location
          {targetFile && (
            <span className="doc-upload__target-badge">
              {targetTitle}
            </span>
          )}
        </button>

        {showTarget && (
          <div className="doc-upload__target-panel">
            <p className="doc-upload__target-hint">
              Leave blank to let the pipeline auto-detect related notes. Choose a note below to merge the proposal directly into it.
            </p>

            {/* Clear button */}
            {targetFile && (
              <button
                className="doc-upload__target-clear"
                onClick={() => { setTargetFile(''); setTargetTitle(''); }}
                type="button"
              >
                ✕ Clear — use auto-detect
              </button>
            )}

            {/* Search filter */}
            <input
              type="text"
              className="doc-upload__target-search"
              placeholder="Filter notes…"
              value={fileFilter}
              onChange={(e) => setFileFilter(e.target.value)}
            />

            {/* File list */}
            <div className="doc-upload__target-list">
              {vaultLoading && <span className="doc-upload__target-loading">Loading vault…</span>}
              {!vaultLoading && filteredFiles.length === 0 && (
                <span className="doc-upload__target-loading">No notes found</span>
              )}
              {!vaultLoading && filteredFiles.map((f) => (
                <button
                  key={f.file_path}
                  className={`doc-upload__target-item ${targetFile === f.file_path ? 'doc-upload__target-item--selected' : ''}`}
                  onClick={() => {
                    if (targetFile === f.file_path) {
                      setTargetFile('');
                      setTargetTitle('');
                    } else {
                      setTargetFile(f.file_path);
                      setTargetTitle(f.title);
                    }
                  }}
                  type="button"
                  title={f.file_path}
                >
                  <span className="doc-upload__target-item-name">{f.title}</span>
                  <span className="doc-upload__target-item-path">
                    {f.file_path.split('/').slice(0, -1).join('/')}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="doc-upload__error">
          Error: {(error as Error).message}
        </div>
      )}
    </div>
  );
}
